import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { 
    FaArrowLeft, 
    FaSort,
    FaFileAlt,
    FaFilePdf,
    FaSearch,
    FaPrint,
    FaFileExcel,
    FaCopy
} from 'react-icons/fa';

const ZpHeadRecommendationLetter = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Determine if we are looking at Engineers or Societies based on the path
    const isSociety = location.pathname.includes('recommendation-letter-society');
    const departmentStr = isSociety ? 'society' : 'subhe';
    const pageTitle = isSociety ? "मजूर संख्या शिफारस पत्र यादी" : "सु.बे. अभियंता शिफारस पत्र यादी";

    const [registrations, setRegistrations] = useState([]);
    const [meetings, setMeetings] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [appliedDate, setAppliedDate] = useState('');
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        setLoading(true);

        // Fetch Meetings
        const meetingsRef = ref(db, 'ekamwatapzp/Meetings');
        onValue(meetingsRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const meetingList = Object.entries(data).map(([key, val]) => ({
                    id: key,
                    date: val.lotteryDate || key
                }));
                setMeetings(meetingList.reverse());
            }
        });

        // Fetch Registrations
        const regPath = isSociety ? 'ekamwatapzp/SocietyRegistrations' : 'ekamwatapzp/EngineerRegistrations';
        const regRef = ref(db, regPath);

        onValue(regRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const regs = Object.entries(data).map(([key, val]) => ({
                    ...val,
                    _key: key
                }));
                setRegistrations(regs);
            }
            setLoading(false);
        });

    }, [isSociety]);

    const handleSearch = () => {
        setAppliedDate(selectedDate);
    };

    // Filter and Process Data
    const displayData = useMemo(() => {
        if (!appliedDate) return [];

        const results = [];
        registrations.forEach(reg => {
            if (reg.GeneratedLottery && reg.GeneratedLottery[appliedDate]) {
                const work = reg.GeneratedLottery[appliedDate];
                results.push({
                    id: work.id || work.workNumber,
                    taluka: work.taluka || reg.taluka || "-",
                    schemeName: work.schemeName || "-",
                    workName: work.workName || "-",
                    winnerName: reg.firstName ? `${reg.firstName} ${reg.surname}` : (reg.fullName || reg.societyName || reg.name || "-"),
                    fullReg: reg,
                    workData: work
                });
            }
        });

        // Apply Search Term
        if (searchTerm) {
            return results.filter(item => 
                item.taluka.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.schemeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.workName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.winnerName.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        return results;
    }, [registrations, appliedDate, searchTerm]);

    const handleViewLetter = (item) => {
        // Prepare dummy currentUser for SelectionLetter
        const winnerData = {
            ...item.fullReg,
            id: item.fullReg._key
        };
        
        // SelectionLetter expects workData and draws user from localStorage usually, 
        // but let's see how it's implemented. It uses localStorage.getItem('currentUser')
        // This is tricky from ZP Head view. 
        // I'll mock the localStorage or pass state if SelectionLetter can handle it.
        // For now, I'll navigate with state.
        navigate('/selection-letter', { 
            state: { 
                workData: {
                    ...item.workData,
                    meetingNumber: appliedDate
                },
                isZpHeadView: true,
                winnerUser: winnerData 
            } 
        });
    };

    if (loading) {
        return (
            <div className="p-20 flex flex-col items-center justify-center space-y-4 font-marathi">
                <div className="animate-spin text-[#22165f] text-4xl">
                    <FaSort />
                </div>
                <p className="text-gray-400 font-bold animate-pulse uppercase tracking-widest text-xs">माहिती लोड होत आहे...</p>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 bg-gray-50/50 min-h-screen font-marathi">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4 px-2 no-print">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate(-1)}
                        className="p-2.5 bg-white rounded-xl shadow-sm border border-gray-100 text-gray-400 hover:text-[#22165f] transition active:scale-95"
                    >
                        <FaArrowLeft />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-800 tracking-tight">{pageTitle}</h1>
                </div>
                <div className="flex items-center gap-2 text-[12px] font-bold text-gray-400 uppercase tracking-wider">
                    Zilla Parishad / <span className="text-gray-600 font-black">{pageTitle}</span>
                </div>
            </div>

            {/* Filter Card */}
            <div className="bg-white rounded shadow-sm border border-gray-100 p-8 mb-8 no-print">
                <div className="flex flex-col gap-3">
                    <label className="text-sm font-bold text-gray-700">
                        लॉटरीची तारीख निवडा <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-col md:flex-row gap-4 max-w-2xl">
                        <select 
                            className="flex-grow bg-white border border-gray-200 rounded py-2.5 px-4 focus:ring-2 focus:ring-blue-500/20 outline-none transition font-medium"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                        >
                            <option value="">लॉटरीची तारीख निवडा</option>
                            {meetings.map(m => (
                                <option key={m.id} value={m.id}>{m.date}</option>
                            ))}
                        </select>
                        <button 
                            onClick={handleSearch}
                            className="bg-[#4e73df] hover:bg-blue-700 text-white px-10 py-2.5 rounded font-bold shadow-md transition transform active:scale-95 whitespace-nowrap"
                        >
                            Search
                        </button>
                    </div>
                </div>
            </div>

            {/* Actions & Search Toggle */}
            <div className="bg-white rounded-t border border-gray-100 p-4 border-b-0 flex flex-col md:flex-row justify-between items-center gap-4 no-print">
                <div className="flex items-center gap-1">
                    <button className="bg-[#6c757d] text-white px-4 py-1.5 rounded text-sm font-bold hover:bg-gray-700 transition flex items-center gap-2">
                        <FaCopy /> Copy
                    </button>
                    <button className="bg-[#6c757d] text-white px-4 py-1.5 rounded text-sm font-bold hover:bg-gray-700 transition flex items-center gap-2">
                        <FaFileExcel /> Excel
                    </button>
                    <button onClick={() => window.print()} className="bg-[#6c757d] text-white px-4 py-1.5 rounded text-sm font-bold hover:bg-gray-700 transition flex items-center gap-2">
                        <FaPrint /> Print
                    </button>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-gray-600">Search:</span>
                    <input 
                        type="text"
                        className="border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Table Section */}
            <div className="bg-white rounded-b shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-[#f8f9fa] border-b border-gray-200">
                            <tr className="text-[13px] font-black text-gray-600">
                                <th className="px-6 py-4 border-r border-gray-100 w-16 text-center"># <FaSort className="inline ml-1 opacity-30 text-[10px]" /></th>
                                <th className="px-6 py-4 border-r border-gray-100 min-w-[120px]">तालुका <FaSort className="inline ml-1 opacity-30 text-[10px]" /></th>
                                <th className="px-6 py-4 border-r border-gray-100 min-w-[150px]">योजनेचे नाव <FaSort className="inline ml-1 opacity-30 text-[10px]" /></th>
                                <th className="px-6 py-4 border-r border-gray-100 min-w-[300px]">कामाचे नाव <FaSort className="inline ml-1 opacity-30 text-[10px]" /></th>
                                <th className="px-6 py-4 border-r border-gray-100 min-w-[200px]">
                                    {isSociety ? "मजूर संस्थेचे नाव" : "सु.बे. अभियंत्याचे नाव"} <FaSort className="inline ml-1 opacity-30 text-[10px]" />
                                </th>
                                <th className="px-6 py-4 text-center w-24 whitespace-nowrap">पत्र <FaSort className="inline ml-1 opacity-30 text-[10px]" /></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {displayData.map((item, index) => (
                                <tr key={index} className="hover:bg-blue-50/10 transition-colors text-sm">
                                    <td className="px-6 py-4 border-r border-gray-50 text-center font-bold text-gray-400">{index + 1}</td>
                                    <td className="px-6 py-4 border-r border-gray-50 font-medium text-gray-700">{item.taluka}</td>
                                    <td className="px-6 py-4 border-r border-gray-50 font-medium text-gray-700">{item.schemeName}</td>
                                    <td className="px-6 py-4 border-r border-gray-50 font-medium text-gray-800 leading-relaxed">
                                        <div className="flex flex-col gap-0.5">
                                            <span>{item.workName}</span>
                                            <span className="text-gray-500 font-bold text-[11px]">Rs- {item.workData?.tenderAmount || item.fullReg?.work_payment?.[appliedDate]?.amount || "-"}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 border-r border-gray-50 font-bold text-gray-900">
                                        {item.winnerName}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <button 
                                            onClick={() => handleViewLetter(item)}
                                            className="text-[#4e73df] hover:text-blue-800 transition p-2 bg-blue-50/50 rounded no-print"
                                        >
                                            <FaPrint size={18} />
                                        </button>
                                        <FaPrint size={18} className="hidden print:inline text-[#4e73df] mx-auto" />
                                    </td>
                                </tr>
                            ))}
                            {displayData.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="px-6 py-10 text-center text-gray-400 font-bold bg-gray-50/50 italic">
                                        No data available in table
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer Info */}
                <div className="p-4 border-t border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4 text-xs font-bold text-gray-500 no-print">
                    <div>
                        Showing {displayData.length > 0 ? 1 : 0} to {displayData.length} of {displayData.length} entries
                    </div>
                    <div className="flex items-center gap-0.5">
                        <button className="px-3 py-1.5 border border-gray-200 rounded-l hover:bg-gray-50 disabled:opacity-50">Previous</button>
                        <button className="px-3 py-1.5 border border-gray-200 rounded-r hover:bg-gray-50 disabled:opacity-50">Next</button>
                    </div>
                </div>
            </div>

            <style>{`
                .font-marathi {
                    font-family: 'Hind', sans-serif;
                }
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
                
                @media print {
                    .no-print { display: none !important; }
                    body { background: white !important; margin: 0; padding: 0; }
                    .bg-white { box-shadow: none !important; border: none !important; }
                    table { border: 1px solid #ddd !important; border-collapse: collapse !important; width: 100% !important; }
                    th, td { border: 1px solid #ddd !important; padding: 10px !important; }
                }
            `}</style>
        </div>
    );
};

export default ZpHeadRecommendationLetter;
